import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { com, kotlin, Nullable } from '@eidu/web'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import SortableFieldRow from './SortableFieldRow'
import useEntityTypes, { useSortedEntityTypes } from '../../io/useEntityTypes'
import { useFetchEntityType } from '../../io/useEntityType'
import { useCreateEntityType, useDeleteEntityType, useModifyEntityType } from '../../io/useEntityTypeMutations'
import FieldData, {
  ExistingFieldData,
  fieldDataFromExistingField,
  fieldDataFromNewField,
} from '../../domain/entity/FieldData'
import TrimmedTextField from '../../components/input/TrimmedTextField'
import LabelInput from '../../components/input/LabelInput'
import KindInput from '../../components/input/KindInput'
import { createEntityLabel, labelLineToString, stringToLabelLine } from '../../components/misc/LabelUtil'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import {
  fieldOrderChanged as computeFieldOrderChanged,
  prepareFieldNameUpdates,
  prepareFieldOrder,
  prepareFieldsToCreate,
  prepareFieldsToDelete,
} from '../../util/entityType/updateHelpers'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import Field = com.eidu.sharedlib.entity.field.Field
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import fieldIdFromString = com.eidu.sharedlib.entity.field.fieldIdFromString
import EntityTypeValidator = com.eidu.sharedlib.entity.validation.EntityTypeValidator
import TypeValidationError = com.eidu.sharedlib.entity.validation.TypeValidationError
import LabelTypeValidationError = com.eidu.sharedlib.entity.validation.LabelTypeValidationError
import KtList = kotlin.collections.KtList

type ManageEntityTypeFormProps = {
  entityTypeId: EntityTypeId | undefined
  entityType: EntityType | null
}

type ValidationState = {
  nameErrors?: readonly TypeValidationError[]
  fieldErrors?: Nullable<readonly TypeValidationError[]>[]
  primaryLabelErrors?: readonly TypeValidationError[]
  secondaryLabelErrors?: readonly TypeValidationError[]
  formError?: string
}

const fieldRowId = (field: FieldData): string => field.field.id.toString()

const createNewFieldData = (existingFields: readonly FieldData[]): FieldData => {
  const existingNames = new Set(existingFields.map((field) => field.field.name))
  let counter = existingFields.length + 1
  let fieldName = `Field ${counter}`
  while (existingNames.has(fieldName)) {
    counter += 1
    fieldName = `Field ${counter}`
  }
  const newField = Field.createSimple(fieldIdFromString(crypto.randomUUID()), fieldName, FieldType.Text, false, null)
  return fieldDataFromNewField(newField, existingFields.length)
}

const validateLabelLine = (
  value: string,
  fields: readonly FieldData[],
  allowReferences: boolean
): readonly TypeValidationError[] | undefined => {
  if (value.trim() === '') return undefined
  const labelLine = stringToLabelLine(value, fields)
  if (!labelLine) return [new LabelTypeValidationError('Label contains invalid field references')]
  return EntityTypeValidator.validateLabelLineWithFieldsList(
    labelLine,
    KtList.fromJsArray(fields.map((it) => it.field)),
    allowReferences
  )?.asJsReadonlyArrayView()
}

const validateState = (
  entityTypeName: string,
  fields: readonly FieldData[],
  primaryLabel: string,
  secondaryLabel: string
): ValidationState => {
  const allFields = fields.map((field) => field.field)
  const nameErrors = EntityTypeValidator.validateNameString(entityTypeName)?.asJsReadonlyArrayView()
  const fieldValidationResults = fields.map(
    (field) =>
      EntityTypeValidator.validateField(field.field, KtList.fromJsArray(allFields))?.asJsReadonlyArrayView() ?? null
  ) as Nullable<readonly TypeValidationError[]>[]

  const primaryLabelErrors = validateLabelLine(primaryLabel, fields, false)
  const secondaryLabelErrors = validateLabelLine(secondaryLabel, fields, true)

  let formError: string | undefined
  if (fields.length === 0) formError = 'At least one field is required.'
  else if (primaryLabel.trim() === '') formError = 'Primary label is required.'

  return { nameErrors, fieldErrors: fieldValidationResults, primaryLabelErrors, secondaryLabelErrors, formError }
}

type InitialFormState = {
  entityTypeName: string
  primaryLabel: string
  secondaryLabel: string
  kind: EntityTypeKind
  fields: readonly FieldData[]
  originalFields: readonly FieldData[]
}

const computeInitialFormState = (entityType: EntityType | null): InitialFormState => {
  if (!entityType) {
    return {
      entityTypeName: '',
      primaryLabel: '',
      secondaryLabel: '',
      kind: EntityTypeKind.Companion.Generic,
      fields: [],
      originalFields: [],
    }
  }
  const existingFieldData = entityType.fields
    .asJsReadonlyArrayView()
    .map((field) => fieldDataFromExistingField(field, undefined)) as readonly ExistingFieldData[]
  const fieldList = entityType.fields.asJsReadonlyArrayView()
  return {
    entityTypeName: entityType.name,
    primaryLabel: labelLineToString(entityType.label.primary, fieldList),
    secondaryLabel: entityType.label.secondary?.let((line) => labelLineToString(line, fieldList)) ?? '',
    kind: entityType.kind,
    fields: existingFieldData,
    originalFields: existingFieldData,
  }
}

const ManageEntityTypeForm = ({ entityTypeId, entityType }: ManageEntityTypeFormProps) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()

  const { error: typesError } = useEntityTypes()
  const sortedEntityTypes = useSortedEntityTypes()
  const fetchEntityType = useFetchEntityType()
  const createMutation = useCreateEntityType()
  const modifyMutation = useModifyEntityType()
  const deleteMutation = useDeleteEntityType()

  const repositoryError = typesError ? String(typesError) : undefined

  const [initial] = useState(() => computeInitialFormState(entityType))
  const [entityTypeName, setEntityTypeName] = useState(initial.entityTypeName)
  const [primaryLabel, setPrimaryLabel] = useState(initial.primaryLabel)
  const [secondaryLabel, setSecondaryLabel] = useState(initial.secondaryLabel)
  const [kind, setKind] = useState<EntityTypeKind>(initial.kind)
  const [fields, setFields] = useState<readonly FieldData[]>(initial.fields)
  const [originalFields] = useState<readonly FieldData[]>(initial.originalFields)
  const [validation, setValidation] = useState<ValidationState>({})
  const [submitting, setSubmitting] = useState(false)
  const [fieldPendingDeletionIndex, setFieldPendingDeletionIndex] = useState<number | null>(null)

  const availableEntityTypes = sortedEntityTypes ?? []

  const updateFieldAt = async (index: number, update: (field: FieldData) => FieldData) => {
    const updated = fields.map((field, currentIndex) => (currentIndex === index ? update(field) : field))
    setFields(updated)
  }

  const handleFieldTypeChange = async (index: number, typeName: string) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withType(FieldType.valueOf(typeName)),
    }))
  }

  const handleFieldRequiredChange = async (index: number) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withRequired(!field.field.required),
    }))
  }

  const handleFieldReferenceChange = async (index: number, referenceTypes: readonly EntityTypeId[]) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withValidReferenceTypes(
        referenceTypes.length > 0 ? kotlin.collections.KtSet.fromJsSet(new Set(referenceTypes)) : undefined
      ),
    }))
  }

  const handleFieldNameChange = async (index: number, newName: string) => {
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withName(newName),
    }))
  }

  const addField = () => {
    setFields((current) => {
      const next = [...current, createNewFieldData(current)]
      if (!entityTypeId && primaryLabel.trim() === '') {
        setPrimaryLabel(`{${next[0].field.name}}`)
      }
      return next
    })
  }

  const removeField = (index: number) => {
    setFields((current) => current.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleFieldReorder = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFields((current) => {
      const oldIndex = current.findIndex((field) => fieldRowId(field) === active.id)
      const newIndex = current.findIndex((field) => fieldRowId(field) === over.id)
      if (oldIndex < 0 || newIndex < 0) return current
      return arrayMove([...current], oldIndex, newIndex)
    })
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleFieldRemoval = (index: number) => {
    const field = fields[index]
    if (!field) return
    if (field.existing) {
      setFieldPendingDeletionIndex(index)
      return
    }
    removeField(index)
  }

  const closeFieldDeletionDialog = () => setFieldPendingDeletionIndex(null)

  const confirmFieldDeletion = () => {
    if (fieldPendingDeletionIndex === null) return
    removeField(fieldPendingDeletionIndex)
    setFieldPendingDeletionIndex(null)
  }

  const deleteType = async () => {
    if (!entityTypeId) return
    const type = await fetchEntityType(entityTypeId)
    if (type === null) {
      showSnackBar('Type not found', 'error')
      return
    }
    setSubmitting(true)
    try {
      await deleteMutation.mutateAsync(entityTypeId)
      showSnackBar(`Deleted type ${type.name}`, 'success')
      navigate('/types')
    } catch (_error) {
      showSnackBar('Failed to delete type', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    const currentValidation = validateState(entityTypeName, fields, primaryLabel, secondaryLabel)
    setValidation(currentValidation)
    if (
      currentValidation.formError ||
      currentValidation.nameErrors ||
      currentValidation.primaryLabelErrors ||
      currentValidation.secondaryLabelErrors ||
      currentValidation.fieldErrors?.some((it) => it && it.length > 0)
    )
      return

    const label = createEntityLabel({ primaryLabel, secondaryLabel, fields })
    const fieldsToCreate = prepareFieldsToCreate(fields)
    const fieldsToUpdate = prepareFieldNameUpdates(fields, originalFields)
    const fieldsToDelete = prepareFieldsToDelete(fields, originalFields)
    const fieldOrder = prepareFieldOrder(fields)
    const fieldOrderChanged = computeFieldOrderChanged(fields, originalFields)

    setSubmitting(true)
    try {
      if (entityTypeId) {
        await modifyMutation.mutateAsync({
          id: entityTypeId,
          entityTypeName: entityTypeName !== entityType?.name ? entityTypeName : undefined,
          label,
          fieldsToCreate,
          fieldsToUpdate,
          fieldsToDelete,
          fieldOrder,
          fieldOrderChanged,
        })
        showSnackBar('Type saved', 'success')
      } else {
        if (!label) {
          showSnackBar('Primary label is required', 'error')
          setSubmitting(false)
          return
        }
        await createMutation.mutateAsync({ name: entityTypeName, fieldsToCreate, label, kind })
        showSnackBar('Type created', 'success')
      }
      navigate(-1)
    } catch (_error) {
      showSnackBar('Failed to save type', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const pendingDeletionFieldName =
    fieldPendingDeletionIndex !== null ? fields[fieldPendingDeletionIndex]?.field.name ?? 'this field' : 'this field'

  return (
    <>
      <Stack padding={3} spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">{entityTypeId ? 'Edit Type' : 'Create Type'}</Typography>
          {entityTypeId && (
            <ConfirmationDialog
              title="Are you sure you want to delete this type? This action cannot be undone."
              content={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Delete fontSize="small" />
                  <Typography>Delete type</Typography>
                </Stack>
              }
              onConfirm={deleteType}
            />
          )}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h6">Name</Typography>
          <TrimmedTextField
            required
            label="Type name"
            value={entityTypeName}
            onChange={(event) => setEntityTypeName(event.target.value)}
            error={!!validation.nameErrors && validation.nameErrors.length > 0}
            helperText={validation.nameErrors?.map((error) => error.message).join(' • ')}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h6">Kind</Typography>
          <KindInput kind={kind} setKind={setKind} editable={!entityTypeId} />
        </Stack>

        <Divider />

        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">Fields</Typography>
            <Button startIcon={<Add />} variant="outlined" onClick={addField}>
              Add field
            </Button>
          </Stack>

          {fields.length === 0 && (
            <Typography color="text.secondary">Add at least one field to define the type.</Typography>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFieldReorder}>
            <SortableContext items={fields.map((field) => fieldRowId(field))} strategy={verticalListSortingStrategy}>
              <Stack spacing={3}>
                {fields.map((fieldData, index) => (
                  <SortableFieldRow
                    key={fieldRowId(fieldData)}
                    id={fieldRowId(fieldData)}
                    index={index}
                    fieldData={fieldData}
                    availableEntityTypes={availableEntityTypes}
                    submitting={submitting}
                    errors={validation.fieldErrors?.[index] ?? undefined}
                    onRemove={() => handleFieldRemoval(index)}
                    onFieldTypeChange={(typeName) => handleFieldTypeChange(index, typeName)}
                    onRequiredChange={() => handleFieldRequiredChange(index)}
                    onReferenceTypesChange={(types) => handleFieldReferenceChange(index, types)}
                    onFieldNameChange={(newName) => handleFieldNameChange(index, newName)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Stack>

        <Divider />

        <LabelInput
          fields={fields}
          primaryLabel={primaryLabel}
          setPrimaryLabel={setPrimaryLabel}
          primaryLabelErrors={validation.primaryLabelErrors}
          secondaryLabel={secondaryLabel}
          setSecondaryLabel={setSecondaryLabel}
          secondaryLabelErrors={validation.secondaryLabelErrors}
          primaryLabelEditable
          secondaryLabelEditable
        />

        <Collapse in={!!validation.formError}>
          <Alert severity="error">{validation.formError}</Alert>
        </Collapse>

        <Collapse in={!!repositoryError}>
          <Alert severity="error">{repositoryError}</Alert>
        </Collapse>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate(-1)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size="1em" color="inherit" /> : undefined}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>
      <Dialog open={fieldPendingDeletionIndex !== null} onClose={closeFieldDeletionDialog}>
        <DialogTitle>Confirm your action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Deleting the field "${pendingDeletionFieldName}" will remove it from this type. This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFieldDeletionDialog}>Cancel</Button>
          <Button color="error" onClick={confirmFieldDeletion}>
            Delete field
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ManageEntityTypeForm
